import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if setup is needed
        const setupRes = await axios.get(`${API_URL}/api/auth/check-setup`);
        if (setupRes.data.needs_setup) {
          setNeedsSetup(true);
          setLoading(false);
          return;
        }

        const storedToken = localStorage.getItem("token");
        if (storedToken) {
          try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` },
            });
            setUser(response.data);
            setToken(storedToken);
          } catch (error) {
            localStorage.removeItem("token");
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error("Auth init error:", error);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      username,
      password,
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const setupAdmin = async (username, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/setup-admin`, {
      username,
      password,
      name,
      role: "admin",
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    setNeedsSetup(false);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
  };

  const hasPermission = (section) => {
    if (!user) return false;
    return user.permissions?.includes(section) || user.role === "admin";
  };

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`,
  });

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        token, 
        loading, 
        needsSetup, 
        login, 
        setupAdmin, 
        logout, 
        updateUser,
        hasPermission,
        getAuthHeader 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
